import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  IoDocumentAttachOutline, IoLinkOutline, IoImageOutline, IoSend, IoCheckmarkCircle,
  IoArrowBack, IoLockClosedOutline, IoLockOpenOutline,
} from "react-icons/io5";
import { RED } from "@/components/DojoShell";
import { adminRespondCarnet, replyCarnetEntry, adminCloseReview, uploadCarnetFile, carnetFileUrl } from "@/lib/api";
import VoiceRecorder from "@/components/VoiceRecorder";

function FilePreview({ att }) {
  const [url, setUrl] = useState(null);
  const ext = ((att.filename || "").split(".").pop() || "").toLowerCase();
  const isImg = (att.content_type || "").startsWith("image/") || att.kind === "image" || ["jpg", "jpeg", "png", "gif", "webp", "bmp", "svg", "heic", "heif"].includes(ext);
  const isAudio = (att.content_type || "").startsWith("audio/") || att.kind === "audio" || ["mp3", "wav", "m4a", "ogg", "aac", "webm"].includes(ext);
  useEffect(() => {
    if (!att.file_id) return undefined;
    let created = null, live = true;
    carnetFileUrl(att.file_id).then((u) => { if (live) { created = u; setUrl(u); } }).catch(() => {});
    return () => { live = false; if (created) URL.revokeObjectURL(created); };
  }, [att.file_id]);
  if (att.kind === "link" || (att.url && !att.file_id)) {
    return <a href={att.url} target="_blank" rel="noreferrer" style={sx.attLink} data-testid="review-attachment"><IoLinkOutline size={15} /> {att.url}</a>;
  }
  if (!url) return <span style={{ ...sx.attLink, opacity: 0.6, cursor: "default" }} data-testid="review-attachment"><IoDocumentAttachOutline size={15} /> Chargement…</span>;
  if (isAudio) return <audio controls src={url} style={{ width: "100%", maxWidth: 320 }} data-testid="review-attachment" />;
  if (isImg) return <a href={url} target="_blank" rel="noreferrer" data-testid="review-attachment"><img src={url} alt={att.filename} style={sx.attImg} /></a>;
  return <a href={url} target="_blank" rel="noreferrer" style={sx.attLink} data-testid="review-attachment"><IoDocumentAttachOutline size={15} /> {att.filename || "Document"}</a>;
}

function statusFor(r, role) {
  if (r.review_closed) return { bg: "#e8f7ec", fg: "#1a7f37", label: role === "student" ? "Terminée" : "Terminé" };
  if (r.sensei_response) return { bg: "color-mix(in srgb, var(--brand) 14%, transparent)", fg: RED, label: "En cours" };
  return { bg: "#fff3e0", fg: "#b26a00", label: role === "student" ? "En attente" : "Non lu" };
}

/** Espace d'échange dédié à UNE relecture — partagé Sensei (role=sensei) et élève (role=student). */
export default function ReviewSpace({ review: r, role = "student", onBack, onChanged }) {
  const [text, setText] = useState("");
  const [atts, setAtts] = useState([]);
  const [linkUrl, setLinkUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const isSensei = role === "sensei";
  const closed = !!r.review_closed;
  const thread = r.thread && r.thread.length ? r.thread
    : (r.sensei_response ? [{ id: "legacy", author: "sensei", name: r.sensei_name || "Sensei", text: r.sensei_response, attachments: r.sensei_attachments || [], created_at: r.responded_at }] : []);

  const addFile = async (e, asImage) => {
    const f = e.target.files?.[0]; if (!f) return;
    try {
      const up = await uploadCarnetFile(f);
      setAtts((a) => [...a, { kind: asImage ? "image" : "file", file_id: up.file_id, filename: up.filename, content_type: up.content_type }]);
      toast.success("Pièce jointe ajoutée");
    } catch { toast.error("Upload impossible (max 10 Mo)"); }
    e.target.value = "";
  };
  const addLink = () => { if (!linkUrl.trim()) return; setAtts((a) => [...a, { kind: "link", url: linkUrl.trim() }]); setLinkUrl(""); };
  const removeAtt = (i) => setAtts((a) => a.filter((_, x) => x !== i));

  const send = async () => {
    if (!text.trim() && atts.length === 0) { toast.error("Écris un message ou ajoute une pièce jointe."); return; }
    setBusy(true);
    try {
      if (isSensei) await adminRespondCarnet(r.id, text.trim(), atts);
      else await replyCarnetEntry(r.id, text.trim(), atts);
      setText(""); setAtts([]);
      toast.success(isSensei ? "Relecture envoyée à l'élève ✓" : "Réponse envoyée au Sensei ✓");
      onChanged && onChanged();
    } catch { toast.error("Envoi impossible"); } finally { setBusy(false); }
  };
  const toggleClose = async () => {
    setBusy(true);
    try { await adminCloseReview(r.id, !closed); toast.success(closed ? "Relecture rouverte" : "Relecture clôturée ✓"); onChanged && onChanged(); }
    catch { toast.error("Action impossible"); } finally { setBusy(false); }
  };

  const st = statusFor(r, role);
  const who = isSensei ? (r.member_name || r.member_email) : (r.title);
  return (
    <div style={sx.space} data-testid={`review-space-${r.id}`}>
      <div style={sx.spaceHead}>
        <button style={sx.backBtn} onClick={onBack} data-testid="review-space-back"><IoArrowBack size={17} /> Retour</button>
        <span style={{ ...sx.status, background: st.bg, color: st.fg }} data-testid="review-space-status">{st.label}</span>
        {isSensei && (
          <button style={{ ...sx.closeReviewBtn, ...(closed ? sx.reopenBtn : {}) }} disabled={busy} onClick={toggleClose} data-testid="review-space-close">
            {closed ? <><IoLockOpenOutline size={16} /> Rouvrir</> : <><IoLockClosedOutline size={16} /> Clôturer la relecture</>}
          </button>
        )}
      </div>

      <div style={sx.spaceScroll}>
        {isSensei && (
          <div style={sx.spaceMember}>
            {r.member_avatar ? <img src={r.member_avatar} alt="" style={sx.avatarLg} /> : <span style={sx.avatarLgFb}>{(r.member_name || r.member_email || "?").slice(0, 1).toUpperCase()}</span>}
            <div style={{ minWidth: 0 }}>
              <div style={sx.spaceMemberName}>{r.member_name || r.member_email}</div>
              <div style={sx.meta}>{r.category_label ? `${r.category_label} · ` : ""}{new Date(r.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}</div>
            </div>
          </div>
        )}

        <div style={sx.entry}>
          <div style={sx.entryLabel}>{isSensei ? "Ce que l'élève a soumis" : "Ta demande de relecture"}</div>
          <div style={sx.entryTitle}>{who}</div>
          {r.body && <div style={sx.entryBody}>{r.body}</div>}
          {r.url && <a href={r.url} target="_blank" rel="noreferrer" style={sx.attLink}><IoLinkOutline size={15} /> {r.url}</a>}
          {r.video_url && (r.video_url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/) || [])[1] && (
            <div style={{ position: "relative", width: "100%", paddingTop: "56.25%", borderRadius: 12, overflow: "hidden", marginTop: 10, background: "#000" }}>
              <iframe style={{ position: "absolute", inset: 0, width: "100%", height: "100%", border: "none" }}
                src={`https://www.youtube.com/embed/${r.video_url.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([A-Za-z0-9_-]{11})/)[1]}`} title={r.title} frameBorder="0" allowFullScreen />
            </div>
          )}
          {r.file_id && <div style={{ marginTop: 8 }}><FilePreview att={{ file_id: r.file_id, filename: r.filename, content_type: r.content_type }} /></div>}
        </div>

        {thread.length > 0 ? (
          <div style={sx.thread} data-testid={`review-thread-${r.id}`}>
            {thread.map((mm, i) => {
              const mine = isSensei ? mm.author === "sensei" : mm.author === "student";
              return (
                <div key={mm.id || i} style={{ ...sx.msg, ...(mine ? sx.msgMine : sx.msgOther) }} data-testid={`review-msg-${mm.author}`}>
                  <div style={sx.msgAuthor}>{mm.author === "student" ? (mm.name || "Élève") : `🐱 ${mm.name || "Sensei"}`}</div>
                  {mm.text && <div style={{ whiteSpace: "pre-wrap", fontSize: 14 }}>{mm.text}</div>}
                  {(mm.attachments || []).map((a, x) => <div key={x} style={{ marginTop: 5 }}><FilePreview att={a} /></div>)}
                </div>
              );
            })}
          </div>
        ) : (
          <div style={sx.emptyThread} data-testid="review-thread-empty">
            {isSensei ? "Pas encore de message. Écris ton premier retour." : "Ta demande est envoyée. Le Sensei te répondra ici."}
          </div>
        )}

        {closed ? (
          <div style={sx.closedNote} data-testid="review-closed-note">
            <IoCheckmarkCircle size={16} /> {isSensei ? "Relecture clôturée. Rouvre-la pour ajouter un message." : "Cette relecture a été clôturée par le Sensei."}
          </div>
        ) : (
          <div style={sx.responder}>
            <label style={sx.lbl}>{isSensei ? "Ta relecture" : "Ta réponse au Sensei"}</label>
            <textarea style={sx.textarea} value={text} onChange={(e) => setText(e.target.value)} placeholder={isSensei ? "Écris ton retour à l'élève…" : "Écris ta réponse…"} data-testid={`review-text-${r.id}`} />
            {atts.length > 0 && (
              <div style={sx.attList}>
                {atts.map((a, i) => (
                  <span key={i} style={sx.attChip} data-testid={`review-att-chip-${i}`}>
                    {a.kind === "link" ? <IoLinkOutline size={13} /> : a.kind === "image" ? <IoImageOutline size={13} /> : <IoDocumentAttachOutline size={13} />}
                    <span style={{ maxWidth: 140, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.filename || a.url}</span>
                    <button onClick={() => removeAtt(i)} style={sx.attX}>×</button>
                  </span>
                ))}
              </div>
            )}
            <div style={sx.tools}>
              <label style={sx.tool}><IoDocumentAttachOutline size={16} /> Document<input type="file" accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.md" style={{ display: "none" }} onChange={(e) => addFile(e, false)} data-testid={`review-file-${r.id}`} /></label>
              <label style={sx.tool}><IoImageOutline size={16} /> Image<input type="file" accept="image/*" style={{ display: "none" }} onChange={(e) => addFile(e, true)} data-testid={`review-image-${r.id}`} /></label>
              <div style={sx.linkRow}>
                <input style={sx.linkInput} placeholder="Coller un lien…" value={linkUrl} onChange={(e) => setLinkUrl(e.target.value)} data-testid={`review-link-input-${r.id}`} />
                <button style={sx.tool} onClick={addLink} data-testid={`review-link-add-${r.id}`}><IoLinkOutline size={16} /> Ajouter</button>
              </div>
              <VoiceRecorder compact testid={`review-voice-${r.id}`} onRecorded={async (file) => {
                if (!file) return;
                try { const up = await uploadCarnetFile(file); setAtts((a) => [...a, { kind: "audio", file_id: up.file_id, filename: up.filename, content_type: up.content_type || "audio/webm" }]); toast.success("Vocal ajouté"); }
                catch { toast.error("Upload du vocal impossible"); }
              }} />
              <button style={{ ...sx.send, opacity: busy ? 0.6 : 1 }} disabled={busy} onClick={send} data-testid={`review-send-${r.id}`}>
                <IoSend size={15} /> Envoyer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const sx = {
  space: { display: "flex", flexDirection: "column", minHeight: "calc(100vh - 200px)" },
  spaceHead: { display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 18 },
  backBtn: { display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 15px", borderRadius: 999, border: "1px solid var(--border-c)", background: "var(--bg-secondary)", color: "var(--text-primary)", fontWeight: 800, fontSize: 13.5, cursor: "pointer" },
  status: { display: "inline-flex", alignItems: "center", gap: 4, padding: "4px 11px", borderRadius: 999, fontSize: 11.5, fontWeight: 800, whiteSpace: "nowrap" },
  closeReviewBtn: { display: "inline-flex", alignItems: "center", gap: 7, padding: "10px 18px", borderRadius: 999, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 13.5, cursor: "pointer", marginLeft: "auto" },
  reopenBtn: { background: "var(--bg-tertiary)", color: "var(--text-primary)", border: "1px solid var(--border-c)" },
  spaceScroll: { display: "flex", flexDirection: "column", gap: 16 },
  spaceMember: { display: "flex", alignItems: "center", gap: 14 },
  avatarLg: { width: 58, height: 58, borderRadius: "50%", objectFit: "cover" },
  avatarLgFb: { width: 58, height: 58, borderRadius: "50%", background: RED, color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 900, fontSize: 22 },
  spaceMemberName: { fontWeight: 900, color: "var(--text-primary)", fontSize: 19, fontFamily: "var(--font-display)" },
  meta: { fontSize: 12.5, color: "var(--text-secondary)" },
  entry: { padding: "18px 20px", borderRadius: 16, background: "var(--bg-primary)", border: "1px solid var(--border-c)" },
  entryLabel: { fontSize: 11, fontWeight: 900, textTransform: "uppercase", letterSpacing: 1, color: "var(--text-secondary)", marginBottom: 8 },
  entryTitle: { fontWeight: 800, color: "var(--text-primary)", fontSize: 17, marginBottom: 8 },
  entryBody: { fontSize: 15, color: "var(--text-secondary)", lineHeight: 1.65, whiteSpace: "pre-wrap" },
  attLink: { display: "inline-flex", alignItems: "center", gap: 6, color: RED, fontWeight: 700, fontSize: 13.5, textDecoration: "none", marginTop: 6, wordBreak: "break-all" },
  thread: { display: "flex", flexDirection: "column", gap: 9 },
  emptyThread: { padding: "22px 18px", textAlign: "center", color: "var(--text-secondary)", border: "1px dashed var(--border-c)", borderRadius: 14, fontSize: 13.5 },
  msg: { padding: "11px 14px", borderRadius: 12, maxWidth: "88%" },
  msgMine: { background: "color-mix(in srgb, var(--brand) 12%, transparent)", border: "1px solid color-mix(in srgb, var(--brand) 25%, transparent)", marginLeft: "auto" },
  msgOther: { background: "var(--bg-tertiary)", border: "1px solid var(--border-c)", marginRight: "auto" },
  msgAuthor: { fontWeight: 800, fontSize: 12, opacity: 0.75, marginBottom: 4, color: "var(--text-primary)" },
  attImg: { maxWidth: 240, maxHeight: 170, borderRadius: 10, border: "1px solid var(--border-c)", display: "block" },
  closedNote: { display: "flex", alignItems: "center", gap: 8, padding: "16px 18px", borderRadius: 14, background: "#e8f7ec", color: "#1a7f37", fontWeight: 700, fontSize: 14 },
  responder: { padding: "18px 20px", borderRadius: 16, border: "1px solid var(--border-c)", background: "var(--bg-secondary)" },
  lbl: { fontSize: 12, fontWeight: 800, color: "var(--text-secondary)", textTransform: "uppercase", letterSpacing: 0.4 },
  textarea: { width: "100%", minHeight: 100, padding: "12px 14px", borderRadius: 12, border: "1.5px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", fontSize: 14.5, fontFamily: "inherit", resize: "vertical", boxSizing: "border-box", marginTop: 6 },
  attList: { display: "flex", flexWrap: "wrap", gap: 8, marginTop: 10 },
  attChip: { display: "inline-flex", alignItems: "center", gap: 6, padding: "5px 8px", borderRadius: 999, background: "var(--bg-tertiary)", border: "1px solid var(--border-c)", fontSize: 12, fontWeight: 700, color: "var(--text-primary)" },
  attX: { border: "none", background: "transparent", color: RED, cursor: "pointer", fontSize: 16, fontWeight: 900, lineHeight: 1 },
  tools: { display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center", marginTop: 12 },
  tool: { display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 12px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", fontWeight: 700, fontSize: 13, cursor: "pointer" },
  linkRow: { display: "flex", gap: 6, alignItems: "center", flex: 1, minWidth: 200 },
  linkInput: { flex: 1, padding: "8px 11px", borderRadius: 10, border: "1px solid var(--border-c)", background: "var(--bg-primary)", color: "var(--text-primary)", fontSize: 13, minWidth: 120 },
  send: { display: "inline-flex", alignItems: "center", gap: 6, padding: "10px 20px", borderRadius: 10, border: "none", background: RED, color: "#fff", fontWeight: 800, fontSize: 14, cursor: "pointer", marginLeft: "auto" },
};
